import { FC } from 'react';

type ReadinessBarProps = {
  /** The user's readiness score (0-100), rendered as the amber fill. */
  fillPercent: number;
  /** The winners target (0-100), rendered as the green marker. */
  targetPercent: number;
  /** Status label, e.g. "Building your score" / "Score ready". */
  statusLabel: string;
  markerLabel?: string;
  /** Header label, e.g. "Your readiness" (pre-GW1) / "Your race" (post-GW1). */
  headerLabel?: string;
  /** Email gate state: checkmark and blue styling on the status label. */
  isReady?: boolean;
};

/**
 * The sticky jeopardy bar (spec 6.1): directional feel only — amber fill
 * for the user's position, green marker for the winners target, never a
 * visible number until the verdict.
 */
const ReadinessBar: FC<ReadinessBarProps> = ({
  fillPercent,
  targetPercent,
  statusLabel,
  markerLabel = 'WINNERS',
  headerLabel = 'Your readiness',
  isReady = false
}) => {
  const clampedFill = Math.max(0, Math.min(100, fillPercent));
  const clampedTarget = Math.max(0, Math.min(100, targetPercent));

  return (
    <div className="bg-neutral-100 px-6 pb-3 pt-1">
      <div className="mx-auto max-w-sm rounded-lg bg-white px-5 py-3 shadow-100">
        <div className="mb-2 flex items-center justify-between">
          <p className="font-body text-xs font-bold uppercase tracking-widest text-primary-500">
            {headerLabel}
          </p>
          <p
            className={`font-heading text-sm font-bold ${
              isReady ? 'text-primary-600' : 'italic text-neutral-600'
            }`}
          >
            {isReady && <span aria-hidden="true">✓ </span>}
            {statusLabel}
          </p>
        </div>
        <div className="relative">
          <div className="h-2.5 overflow-hidden rounded-full bg-neutral-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-700 ease-out"
              style={{ width: `${clampedFill}%` }}
            />
          </div>
          {/* Winners target marker */}
          <div
            className="absolute -top-1 flex -translate-x-1/2 flex-col items-center transition-all duration-700 ease-out"
            style={{ left: `${clampedTarget}%` }}
          >
            <span className="h-[18px] w-0.5 rounded bg-greenfriday" />
          </div>
          <p
            className="absolute top-4 -translate-x-1/2 whitespace-nowrap font-body text-[10px] font-bold tracking-widest text-greenfriday transition-all duration-700 ease-out"
            style={{ left: `${clampedTarget}%` }}
          >
            {markerLabel}
          </p>
        </div>
        {/* Reserve space for the absolutely positioned marker label */}
        <div className="h-5" />
      </div>
    </div>
  );
};

export default ReadinessBar;
